import React from 'react';

import Modal from '@hero/ui-kit/components/Modal';
import PlusIcon from '@hero/ui-kit/icons/utility/PlusIcon';
import UploadIcon from '@hero/ui-kit/icons/utility/UploadIcon';
import CircularButton from '@hero/ui-kit/inputs/CircularButton';
import H from '@hero/ui-kit/typography/H';
import DateFilter from '../../components/DateFilter';
import Layout from '../../components/Layout';
import LimitItems from '../../components/LimitItems';
import ListFilter from '../../components/ListFilter';
import LocationFilter from '../../components/LocationFilter';
import SavedFilters from '../../components/SavedFilters';
import NoResults from '../../components/States/NoResults';
import useColumnVisibility from '../../hooks/useColumnVisibility';
import useOrgStorageKey from '../../hooks/useOrgStorageKey';
import { buildColumnOptions } from '../../utils/tableColumns';
import useExportLeads from './api/useExportLeads';
import useGetAllLeadStates from './api/useGetAllLeadStates';
import useGetLeadsList from './api/useGetLeadsList';
import LeadsFilter from './components/LeadsFilter';
import LeadsForm from './components/LeadsForm';
import StatusFilter from './components/StatusFilter';
import UnreadSmsHeaderButton from './components/UnreadSmsHeaderButton';
import LeadsTable from './LeadsTable';
import {
    FORM_FILL_COLUMNS_STORAGE_KEY,
    FormFillColumnId,
    getFormFillColumns,
    getFormFillGridTemplate
} from './columns';
import { useUserStatusContext } from '../../context/UserStatus';
import useSearchOrgIds from '../../hooks/useSearchOrgIds';
import leadSearchProps from './leadSearchProps';
import useQueryParams from './useQueryParams';
import useStatesQueryParams from './useStatesQueryParams';

const LeadsManagement: React.FC = () => {
    const [createLeadModal, setCreateLeadModal] = React.useState(false);
    const { isLoading: isLoadingLeadStates } = useGetAllLeadStates();

    const { query } = useQueryParams();
    const states = useStatesQueryParams();
    const { exportLeads, loading } = useExportLeads({ ...query, states });
    const { data, mutateAsync: fetchLeads, isPending: isLeadsLoading } = useGetLeadsList();

    const locationParam = 'state';

    const { orgs } = useUserStatusContext();
    const isMultiOrg = orgs.length > 1;

    // [HWM-23014] A search spans every provider the login can reach; browsing without a search
    // term stays scoped to the active provider (the OrgSwitcher's job). The backend drops the
    // orgs whose role lacks lead-view, so sending them all is safe.
    const searchOrgIds = useSearchOrgIds(query?.search);

    const handleFetchLeads = React.useCallback(() => {
        return fetchLeads({ payload: { ...query, states }, heroOrgIds: searchOrgIds });
    }, [query, states, fetchLeads, searchOrgIds]);

    const columns = React.useMemo(() => getFormFillColumns(isMultiOrg), [isMultiOrg]);

    const orgStorageKey = useOrgStorageKey({ perUser: true });
    const { isVisible, toggle } = useColumnVisibility<FormFillColumnId>(
        orgStorageKey(FORM_FILL_COLUMNS_STORAGE_KEY),
        columns
    );

    const columnControl = React.useMemo(
        () => ({
            options: buildColumnOptions(columns, isVisible),
            onToggle: (id: string) => toggle(id as FormFillColumnId)
        }),
        [columns, isVisible, toggle]
    );

    const gridTemplate = React.useMemo(
        () => getFormFillGridTemplate(isVisible, columns),
        [isVisible, columns]
    );

    const isDefaultTab = query?.filter_type === 'default';

    const filters = isDefaultTab ? (
        <>
            <DateFilter inline searchParam="created" defaultLabel="Created" />
            <DateFilter inline searchParam="updated" defaultLabel="Updated" />
            <StatusFilter inline />
            <ListFilter
                inline
                options={[
                    { value: '1', label: 'Hero Member' },
                    { value: '0', label: 'Not a Hero Member' }
                ]}
                searchParam="is_member"
                label="Hero Member"
                mode="single"
                variant="pills"
                resetPaginationKeys={['offset']}
            />
        </>
    ) : undefined;
    const filterParams = isDefaultTab ? ['created', 'updated', 'approval_status', 'is_member'] : [];
    const savedFiltersKey = `ep:saved-filters:form-fills:${query?.filter_type || 'default'}`;

    React.useEffect(() => {
        // we need to wait for unlock lead to finish on lead details
        const timer = setTimeout(() => {
            handleFetchLeads();
        }, 300);
        // Without the clear, a re-run schedules a second POST alongside the pending one and the
        // two can resolve out of order, rendering the older page.
        return () => clearTimeout(timer);
    }, [handleFetchLeads]);

    const headerElements = (
        <>
            <LocationFilter searchParam={locationParam} />
            <UnreadSmsHeaderButton />
            {query?.filter_type === 'default' ? (
                <CircularButton
                    disabled={loading}
                    variant="secondary"
                    isLink={false}
                    onClick={exportLeads}
                    marginLeft="small"
                    className="flex-center"
                    styles={{ opacity: loading ? 0.5 : 1 }}
                    alt="Export Form fills"
                >
                    <UploadIcon size="utility" styles={{ transform: 'rotateZ(180deg)' }} />
                </CircularButton>
            ) : null}

            <CircularButton
                variant="secondary"
                isLink={false}
                onClick={(e) => {
                    e.stopPropagation();
                    setCreateLeadModal(true);
                }}
                marginLeft="small"
                className="flex-center"
                alt="Add Form fill"
            >
                <PlusIcon size="utility" />
            </CircularButton>
        </>
    );

    return (
        <>
            <Layout
                title="Form fills"
                additionalElement={headerElements}
                {...leadSearchProps}
                filters={filters}
                filterParams={filterParams}
                removeQueryParamsOnSearch={[
                    'limit',
                    'offset',
                    'updated',
                    'created',
                    'approval_status',
                    'is_member'
                ]}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2.8rem',
                        minHeight: '4rem',
                        marginBottom: '2.4rem',
                        justifyContent: 'space-between'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2.8rem' }}>
                        <LeadsFilter />
                        <SavedFilters
                            storageKey={savedFiltersKey}
                            ignoreParams={['filter_type']}
                            showLeadingDivider
                        />
                    </div>
                    <LimitItems
                        isLoading={isLeadsLoading}
                        limit={query?.limit ? +query.limit : 10}
                        offset={query?.offset ? +query.offset : 0}
                        total_count={data?.total_count}
                        columnControl={columnControl}
                    />
                </div>

                {data?.leads.length === 0 ? (
                    <NoResults
                        search={query.search || ''}
                        hideSearchLabel={!query.search}
                        searchSupportsLabel="User Name, ID or Email"
                    />
                ) : (
                    <LeadsTable
                        search={query.search || ''}
                        onFetchLeads={handleFetchLeads}
                        data={data}
                        isLoading={isLoadingLeadStates || isLeadsLoading}
                        listQuery={{ ...query, states }}
                        isColumnVisible={isVisible}
                        gridColumns={gridTemplate}
                        columns={columns}
                    />
                )}
            </Layout>

            {createLeadModal && (
                <Modal
                    isCancelable
                    onClose={() => {
                        setCreateLeadModal(false);
                    }}
                    styles={{ padding: '0' }}
                    width="page"
                >
                    <H role="h5" noDefaultMargin>
                        Add Form fill
                    </H>

                    <LeadsForm onSuccess={() => setCreateLeadModal(false)} />
                </Modal>
            )}
        </>
    );
};

export default LeadsManagement;
